<template>
  <div class="w-full flex flex-col items-center lg:p-4 generate-page">
    <div class="max-w-5xl mx-auto w-full px-4 py-12">
      <div class="mb-12 text-center">
        <h1 class="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent">
          {{ t('vocalSeparation.title') }}
        </h1>
        <p class="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">{{ t('vocalSeparation.subtitle') }}</p>
      </div>

      <!-- 输入区域 -->
      <div v-if="!result" class="rounded-xl shadow-lg lg:p-6 space-y-6">
        <!-- 输入方式切换 -->
        <div class="flex gap-2 pb-2">
          <button v-for="inputTab in inputTabs" :key="inputTab.key"
            class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 lg:hover:bg-purple-500 lg:dark:hover:bg-purple-500 transition-colors duration-200"
            :class="{ 'bg-purple-500 dark:bg-purple-500 text-white dark:text-white': currentInputTab === inputTab.key }"
            @click="currentInputTab = inputTab.key">
            <div :class="[inputTab.icon, 'text-lg']" />
            {{ inputTab.label }}
          </button>
        </div>

        <!-- YouTube URL 输入 -->
        <div v-if="currentInputTab === 'url'" class="space-y-4">
          <div>
            <input 
              type="text" 
              v-model="youtubeUrl" 
              class="w-full !pb-3"
              :placeholder="t('vocalSeparation.youtubeUrl.placeholder')"
              @input="validateYoutubeUrl"
            />
            <p v-if="urlError" class="text-red-500 text-sm mt-1">{{ urlError }}</p>
          </div>
        </div>

        <!-- 音频文件上传 -->
        <div v-if="currentInputTab === 'file'" class="space-y-4">
          <div class="w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 text-center hover:border-purple-500 dark:hover:border-purple-500 transition-colors duration-200"
            :class="{ 'border-purple-500 dark:border-purple-500': isDragging }"
            @dragenter.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @dragover.prevent
            @drop.prevent="handleDrop"
            @click="triggerFileInput">
            <input type="file" ref="fileInput" class="hidden" accept="audio/*" @change="handleAudioUpload">
            <div class="space-y-4">
              <div class="flex justify-center">
                <div class="i-lucide-music text-6xl text-gray-400 dark:text-gray-500"></div>
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">
                {{ t('vocalSeparation.file.uploadText') }}
              </div>
              <div v-if="audioFileName" class="mt-4">
                <div class="flex items-center justify-center gap-2">
                  <div class="i-lucide-music text-xl text-purple-500"></div>
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{ audioFileName }}</span>
                </div>
                <button @click.stop="clearAudio" class="mt-2 text-sm text-red-500 hover:text-red-600">
                  {{ t('vocalSeparation.file.clear') }}
                </button>
              </div>
              <div v-if="isUploading" class="mt-4">
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                  <div class="bg-purple-500 h-2 rounded-full transition-all duration-300"
                    :style="{ width: `${uploadProgress}%` }"></div>
                </div>
                <div class="text-sm text-gray-500 dark:text-gray-400">
                  {{ t('vocalSeparation.file.uploading') }} {{ Math.round(uploadProgress) }}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <button 
          type="submit"
          @click="handleStartClick"
          class="w-full py-3 px-4 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 text-white rounded-lg font-medium transition-all duration-200 shadow-lg"
          :class="canStartSeparation ? 'hover:from-purple-700 hover:via-pink-600 hover:to-purple-700 hover:shadow-xl' : 'opacity-60 cursor-not-allowed'"
          :aria-disabled="!canStartSeparation"
        >
          <span v-if="isLoading" class="inline-flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          {{ isLoading ? t('vocalSeparation.processing') : t('vocalSeparation.start') }}
        </button>
      </div>

      <!-- 进度显示区域 -->
      <div v-if="isLoading" class="mt-8">
        <div class="space-y-4">
          <div class="text-center">
            <p class="text-lg font-medium text-gray-700 dark:text-gray-300">{{ currentStatus }}</p>
          </div>
          <div class="relative h-2 bg-gray-200 rounded-full overflow-hidden">
            <div class="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 animate-pulse" 
                 :style="{ width: progress + '%' }"></div>
          </div>
          <div class="flex justify-center">
            <div class="w-64 h-32 relative">
              <!-- 声波动画 -->
              <div class="absolute inset-0 flex items-center justify-center space-x-1">
                <div class="w-1 h-8 rounded-full animate-wave" style="background: linear-gradient(to bottom, #9333ea, #ec4899); animation-delay: 0s"></div>
                <div class="w-1 h-6 rounded-full animate-wave" style="background: linear-gradient(to bottom, #ec4899, #9333ea); animation-delay: 0.1s"></div>
                <div class="w-1 h-10 rounded-full animate-wave" style="background: linear-gradient(to bottom, #9333ea, #ec4899); animation-delay: 0.2s"></div>
                <div class="w-1 h-8 rounded-full animate-wave" style="background: linear-gradient(to bottom, #ec4899, #9333ea); animation-delay: 0.3s"></div>
                <div class="w-1 h-6 rounded-full animate-wave" style="background: linear-gradient(to bottom, #9333ea, #ec4899); animation-delay: 0.4s"></div>
                <div class="w-1 h-10 rounded-full animate-wave" style="background: linear-gradient(to bottom, #ec4899, #9333ea); animation-delay: 0.5s"></div>
                <div class="w-1 h-8 rounded-full animate-wave" style="background: linear-gradient(to bottom, #9333ea, #ec4899); animation-delay: 0.6s"></div>
                <div class="w-1 h-6 rounded-full animate-wave" style="background: linear-gradient(to bottom, #ec4899, #9333ea); animation-delay: 0.7s"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 结果展示区域 -->
      <div v-if="result" class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 space-y-6 text-center">
        <div class="space-y-4">
          <h2 class="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent">
            {{ t('vocalSeparation.resultTitle') }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg transform hover:scale-105 transition-transform duration-300">
              <h3 class="text-lg font-medium mb-4 text-purple-600 dark:text-purple-400">{{ t('vocalSeparation.instrumental') }}</h3>
              <div class="custom-audio-player">
                <audio controls class="w-full">
                  <source :src="result.instrumental_url" type="audio/wav">
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
            <div class="p-6 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg transform hover:scale-105 transition-transform duration-300">
              <h3 class="text-lg font-medium mb-4 text-pink-600 dark:text-pink-400">{{ t('vocalSeparation.vocals') }}</h3>
              <div class="custom-audio-player">
                <audio controls class="w-full">
                  <source :src="result.vocals_url" type="audio/wav">
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
          </div>
        </div>
        <button 
          @click="resetForm"
          class="w-full py-3 px-4 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:from-purple-700 hover:via-pink-600 hover:to-purple-700 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          {{ t('vocalSeparation.tryAgain') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '~/composables/useToast';
import { musicService, API_PATHS_MUSIC } from '~/services/musicService';
import type { VocalSeparationStatusResponse } from '~/services/musicService';
import { useAuthState } from '~/composables/useAuthState';
import { trackEvent } from '~/utils/analytics';

const { t } = useI18n();
const toast = useToast();
const { isAuthenticated } = useAuthState();

// 状态变量
const youtubeUrl = ref('');
const urlError = ref('');
const isLoading = ref(false);
const result = ref<VocalSeparationStatusResponse['data']['result'] | null>(null);
const currentStatus = ref('');
const progress = ref(0);
const taskId = ref('');
const maxRetries = 100; // 最大重试次数
const retryInterval = 4000; // 重试间隔(ms)

// 输入方式切换
const inputTabs = [
  { key: 'url', label: t('vocalSeparation.inputTab.url'), icon: 'i-lucide-link' },
  { key: 'file', label: t('vocalSeparation.inputTab.file'), icon: 'i-lucide-music' },
]
const currentInputTab = ref('url')

// 音频文件上传相关
const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const isUploading = ref(false)
const uploadProgress = ref(0)
const audioFileName = ref('')
const audioFileId = ref('')

// 触发文件选择
const triggerFileInput = () => {
  if (fileInput.value) {
    fileInput.value.click()
  }
}

// 处理拖拽上传
const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    const file = files[0]
    if (file.type.startsWith('audio/')) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('vocalSeparation.file.validation.tooLarge'))
        return
      }
      handleAudioUpload({ target: { files: [file] } } as any)
    } else {
      toast.error(t('vocalSeparation.file.validation.invalidType'))
    }
  }
}

// 处理文件选择
const handleAudioUpload = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files && input.files[0]) {
    const file = input.files[0]
    if (file.type.startsWith('audio/')) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('vocalSeparation.file.validation.tooLarge'))
        return
      }
      await uploadAudioFile(file)
    } else {
      toast.error(t('vocalSeparation.file.validation.invalidType'))
    }
  }
}

// 上传音频文件
const uploadAudioFile = async (file: File) => {
  try {
    isUploading.value = true
    uploadProgress.value = 0
    audioFileName.value = file.name

    const path = isAuthenticated.value ? API_PATHS_MUSIC.UPLOAD.FILE : API_PATHS_MUSIC.UPLOAD.FILE_WITHOUT_AUTH;
    let res = await musicService.uploadFile(path, file, (progress) => {
      uploadProgress.value = progress
    });

    if (res.data.id) {
      audioFileId.value = res.data.id
      toast.success(t('vocalSeparation.file.success'))
    } else {
      throw new Error(res.message || '上传失败')
    }
  } catch (error) {
    console.error('上传失败:', error)
    toast.error(t('vocalSeparation.file.failed'))
    clearAudio()
  } finally {
    isUploading.value = false
    uploadProgress.value = 0
  }
}

// 清除音频文件
const clearAudio = () => {
  audioFileName.value = ''
  audioFileId.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// 验证输入是否有效
const isValidInput = computed(() => {
  if (currentInputTab.value === 'url') {
    return youtubeUrl.value && !urlError.value
  } else {
    return !!audioFileId.value
  }
})

const canStartSeparation = computed(() => {
  return !!isValidInput.value && !isLoading.value && !isUploading.value
})

// 验证Youtube URL
const isValidUrl = computed(() => {
  return youtubeUrl.value && !urlError.value;
});

const validateYoutubeUrl = () => {
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
  if (!youtubeUrl.value) {
    urlError.value = t('vocalSeparation.youtubeUrl.validation.required');
  } else if (!youtubeRegex.test(youtubeUrl.value)) {
    urlError.value = t('vocalSeparation.youtubeUrl.validation.invalid');
  } else {
    urlError.value = '';
  }
  // urlError.value = ''; // 暂时关闭验证
};

const handleStartClick = () => {
  if (isLoading.value) return;

  if (isUploading.value) {
    toast.info(t('vocalSeparation.file.uploading'));
    trackEvent('vocal_separation_blocked_click', {
      input_type: currentInputTab.value,
      reason: 'uploading'
    });
    return;
  }

  if (!isValidInput.value) {
    if (currentInputTab.value === 'url') {
      validateYoutubeUrl();
      toast.error(urlError.value || t('vocalSeparation.error.invalidInput'));
    } else {
      toast.error(t('vocalSeparation.error.invalidInput'));
    }

    trackEvent('vocal_separation_blocked_click', {
      input_type: currentInputTab.value,
      reason: 'invalid_input',
      has_url: !!youtubeUrl.value,
      has_file: !!audioFileId.value
    });
    return;
  }

  startSeparation();
};

// 更新进度状态
const updateStatus = (status: string, progressValue: number) => {
  currentStatus.value = status;
  progress.value = progressValue;
};

// 开始分离
const startSeparation = async () => {
  console.log('startSeparation', isValidInput.value);
  if (!isValidInput.value) {
    toast.error(t('vocalSeparation.error.invalidInput'));
    return;
  }

  // 追踪人声分离开始
  trackEvent('vocal_separation_start', {
    input_type: currentInputTab.value,
    is_authenticated: isAuthenticated.value,
    has_url: currentInputTab.value === 'url' && !!youtubeUrl.value,
    has_file: currentInputTab.value === 'file' && !!audioFileId.value
  });

  isLoading.value = true;
  result.value = null;
  progress.value = 0;
  updateStatus(t('vocalSeparation.status.tips1'), 10);

  try {
    // 调用分离API
    const params = currentInputTab.value === 'url'
      ? { audio_url: youtubeUrl.value }
      : { file_id: audioFileId.value }
    const path = isAuthenticated.value ? API_PATHS_MUSIC.VOCAL_SEPARATION.SPLIT : API_PATHS_MUSIC.VOCAL_SEPARATION.SPLIT_WITHOUT_AUTH;
    const response = await musicService.startVocalSeparation(path,params);
    if (response.code === 200) {
      taskId.value = response.data.result.task_id;
      updateStatus(t('vocalSeparation.status.tips2'), 30);
      await pollTaskStatus();
    } else {
      throw new Error(response.msg);
    }
  } catch (error: any) {
    console.error(error);
    toast.error(t('vocalSeparation.error.general'));
    isLoading.value = false;
    // 追踪人声分离错误
    trackEvent('vocal_separation_error', {
      input_type: currentInputTab.value,
      error: error.message || 'unknown'
    });
  }
};

// 轮询任务状态
const pollTaskStatus = async () => {
  let retryCount = 0;
  
  const poll = async () => {
    if (retryCount >= maxRetries) {
      toast.error(t('vocalSeparation.error.timeout'));
      isLoading.value = false;
      // 追踪人声分离超时
      trackEvent('vocal_separation_timeout', {
        task_id: taskId.value,
        retry_count: retryCount,
        input_type: currentInputTab.value
      });
      return;
    }

    try {
      const path = isAuthenticated.value ? API_PATHS_MUSIC.VOCAL_SEPARATION.STATUS : API_PATHS_MUSIC.VOCAL_SEPARATION.STATUS_WITHOUT_AUTH;
      const response = await musicService.getVocalSeparationStatus(path, { task_id: taskId.value });
      
      if (response.code === 200) {
        const status = response.data.result.status;
        
        switch (status) {
          case 1: // pending
            updateStatus(t('vocalSeparation.status.tips3'), 40);
            break;
          case 2: // processing
            updateStatus(t('vocalSeparation.status.tips4'), 60);
            break;
          case 3: // completed
            updateStatus(t('vocalSeparation.status.tips5'), 100);
            result.value = response.data.result;
            isLoading.value = false;
            // 追踪人声分离成功
            trackEvent('vocal_separation_success', {
              task_id: taskId.value,
              retry_count: retryCount,
              input_type: currentInputTab.value
            });
            return;
          case 4: // failed
            toast.error(t('vocalSeparation.error.failed'));
            isLoading.value = false;
            // 追踪人声分离失败
            trackEvent('vocal_separation_failed', {
              task_id: taskId.value,
              retry_count: retryCount,
              input_type: currentInputTab.value,
              reason: 'task_failed'
            });
            return;
        }

        retryCount++;
        setTimeout(poll, retryInterval);
      } else {
        // 非200状态码，继续重试
        console.warn(`Status check failed (${response.code}): ${response.msg}`);
        retryCount++;
        setTimeout(poll, retryInterval);
      }
    } catch (error: any) {
      // 网络错误或其他异常，继续重试
      console.warn('Status check error:', error);
      retryCount++;
      setTimeout(poll, retryInterval);
    }
  };

  await poll();
};

// 重置表单
const resetForm = () => {
  youtubeUrl.value = '';
  urlError.value = '';
  result.value = null;
  progress.value = 0;
  currentStatus.value = '';
  clearAudio();
};

// SEO配置 - 使用新的composable
const { setVocalSeparationSeo } = useSeo();
setVocalSeparationSeo();
</script>

<style scoped>
.generate-page {
  min-height: calc(100vh - 64px);
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-wave {
  animation: wave 0.8s ease-in-out infinite;
}

/* 自定义音频播放器样式 */
.custom-audio-player {
  @apply relative;
}

.custom-audio-player audio {
  @apply w-full;
}

.custom-audio-player audio::-webkit-media-controls-panel {
  @apply bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-800/30 dark:to-pink-800/30;
}

.custom-audio-player audio::-webkit-media-controls-play-button,
.custom-audio-player audio::-webkit-media-controls-mute-button {
  @apply bg-purple-600 dark:bg-purple-500 rounded-full;
}

.custom-audio-player audio::-webkit-media-controls-current-time-display,
.custom-audio-player audio::-webkit-media-controls-time-remaining-display {
  @apply text-purple-600 dark:text-purple-400;
}

.custom-audio-player audio::-webkit-media-controls-timeline {
  @apply bg-transparent;
}

.custom-audio-player audio::-webkit-media-controls-volume-slider {
  @apply bg-transparent;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes wave {
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.2);
  }
}
</style>
